import {
  BrandImageTag,
  GeneralGoal,
  PENDING_BUSINESS_ID,
  TONE_OF_VOICE_OPTIONS,
  ToneOfVoice,
} from '@mondaygpt/constants'
import type {
  BrandLearningPreviewResponse,
  BrandLearningSource,
  BrandLearningSourceSummary,
  BrandLearningSummary,
  LogoBackdrop,
  OverrideEntry,
} from '@mondaygpt/types'
import { Button, EditableValue } from 'components'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from 'lib/utils'
import { selectBusinessId } from 'modules/authentication'
import {
  clearBrandLearningOverride,
  fetchBrandLearning,
  setBrandLearningOverride,
} from 'modules/brand-learning/api/brand-learning-api'
import { BrandVisualSection } from 'modules/brand-learning/components/BrandVisualSection'
import { BrandVoiceSection } from 'modules/brand-learning/components/BrandVoiceSection'
import { useBrandLearningPusher } from 'modules/brand-learning/hooks/useBrandLearningPusher'
import {
  pickPreferredVisualSource,
  pickPreferredVoiceSource,
} from 'modules/brand-learning/lib/preferred-source'
import { useAppSelector } from 'modules/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Eyebrow } from './Eyebrow'
import { MobileStickyFooter } from './MobileStickyFooter'
import {
  SHARED_LAYOUT_COLOR,
  SHARED_LAYOUT_LOGO,
  SHARED_LAYOUT_NAME,
  SHARED_LAYOUT_TONE,
  TRANSITION_COLOR,
  TRANSITION_LOGO,
  TRANSITION_NAME,
  TRANSITION_TONE,
} from './shared-layout'

/**
 * Stagger entry for non-shared brand-card content. Hoisted so framer doesn't
 * see a fresh object identity on every render. Delays start at 500ms so the
 * shared layoutId elements (logo, name, swatches, tone chips) settle first;
 * then the rest of the card fills in around them.
 */
const fadeInUp = (delay: number) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: [0.2, 0.8, 0.2, 1] as const },
})

import type { BrandImage } from 'modules/creative/types'

/**
 * Brand-learning override edits the user makes during onboarding before the
 * business row exists. Buffered locally + applied optimistically to the
 * displayed chips; persistBrandKit fires them against the real businessId
 * after `createBusinessAction` returns. `value === null` represents a clear
 * (i.e. revert to the mined value) — null is not a legitimate override value
 * for any of the fields the chips edit.
 */
export interface PendingBrandLearningOverride {
  source: BrandLearningSource
  fieldPath: string
  value: unknown | null
}

export interface BrandCardData {
  businessName: string
  industry: string
  description: string
  targetAudience: string
  tones: ToneOfVoice[]
  goals: GeneralGoal[]
  colors?: { primary: string; secondary: string; accent: string }
  logoUrl?: string | null
  /** Backdrop the logo chip renders on (computed server-side). Default 'light'. */
  logoBackdrop?: LogoBackdrop
  /**
   * Brand-relevant keywords produced by the scrape (e.g. "handmade apparel",
   * "Tel Aviv design"). Flushed through `patchBrandKit` so the BrandKit row
   * preserves them — without this they'd be dropped on the floor.
   */
  keywords?: string[]
  /** What the business primarily posts about — picked on the FocusStep. */
  contentFocus: BrandImageTag | null
  /** Brand images uploaded on the FocusStep, tagged by archetype. */
  images: BrandImage[]
  /**
   * Brand-learning chip edits captured while `businessId === PENDING_BUSINESS_ID`.
   * Flushed by `persistBrandKit` after the business row is created. Absent
   * (or empty) when the user reached this step with a real businessId
   * (e.g. additional-business onboarding mode).
   */
  pendingBrandLearningOverrides?: PendingBrandLearningOverride[]
  /**
   * Preview responses captured while `businessId === PENDING_BUSINESS_ID`. T11's
   * persistBrandKit flushes each one via `seedBrandLearningFromPreview` after
   * `patchBrandKit` lands the handle/URL on the just-created brand kit.
   * Absent when the user reached this step with a real businessId (additional-
   * business mode — no preview was needed).
   */
  pendingBrandLearningPreviews?: Array<{
    source: BrandLearningSource
    response: BrandLearningPreviewResponse
    previewedWith: { instagramHandle?: string; websiteUrl?: string }
  }>
}

interface BrandCardStepProps {
  initial: BrandCardData
  scanError?: string
  /** Source of the (failed) scrape — drives the inline error copy. */
  scanSource?: 'website' | 'social' | 'name-only'
  /**
   * Brand-learning previews resolved by the parent during ScanningStep. When
   * provided, the card skips its own preview-fetch effect entirely and renders
   * fully populated on first paint. Absent in the additional-business mode
   * (real businessId, fetched via fetchBrandLearning instead).
   */
  prefetchedBrandLearning?: {
    summary: BrandLearningSummary
    pendingPreviews: Array<{
      source: BrandLearningSource
      response: BrandLearningPreviewResponse
      previewedWith: { instagramHandle?: string; websiteUrl?: string }
    }>
  }
  isSubmitting?: boolean
  /** When true, confirming leads into the conversational chat (not straight into
   *  the app), so the CTA says so instead of "let me in". */
  nextIsChat?: boolean
  onSubmit: (data: BrandCardData) => void
}

const MIN_TONES = 1
const MAX_TONES = 3
// Mirrors the createBusiness server validation (name >= 2 chars). Keep in sync
// so the client blocks before submit instead of surfacing a 400.
const MIN_BUSINESS_NAME = 2

/** Tie the visible `.row-label` to its field so SRs announce the row name. */
const INDUSTRY_LABEL_ID = 'brand-card-industry-label'
const AUDIENCE_LABEL_ID = 'brand-card-audience-label'

/**
 * Phase 3. Single editable card showing what the scrape found. Tap any field
 * to edit. Tone + goal chips toggle on/off (clamped to MAX_*). The CTA stays
 * disabled if a downstream-required field is missing — but the scraper plus
 * sane defaults keep that case rare.
 */
export const BrandCardStep: React.FC<BrandCardStepProps> = ({
  initial,
  scanError,
  scanSource,
  prefetchedBrandLearning,
  isSubmitting,
  nextIsChat,
  onSubmit,
}) => {
  const [data, setData] = useState<BrandCardData>(() => ensureDefaults(initial))

  // Drop the shared layoutIds (logo, name, color swatches, tone chips)
  // ~800ms after mount — long enough for the ScanningStep→BrandCardStep
  // morph to finish (700ms per SHARED_LAYOUT_TRANSITION). After that, the
  // layoutId tags have served their purpose, and keeping them alive lets
  // framer-motion's LayoutGroup leak ghost copies of these elements into
  // later phases (Goals / Focus): when this step unmounts via PhaseFade,
  // the layoutId-tagged spans get orphaned at their last-painted positions
  // and stay visible behind whatever step renders next. Dropping the IDs
  // before unmount makes the spans plain motion components again, so they
  // unmount cleanly with their parent.
  const [morphSettled, setMorphSettled] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setMorphSettled(true), 800)
    return () => clearTimeout(t)
  }, [])

  // Brand-learning summary — shown as the "How you look" section.
  //
  // Two paths:
  //  - Onboarding preview (PENDING_BUSINESS_ID): parent runs the previews
  //    during ScanningStep and hands the resolved summary via
  //    `prefetchedBrandLearning`. We seed `brandLearning` from it and never
  //    fetch locally.
  //  - Real business (additional-business mode): no prefetch, fall back to
  //    `fetchBrandLearning(businessId)` + Pusher refresh on mining completion.
  const businessId = useAppSelector(selectBusinessId)
  const [brandLearning, setBrandLearning] = useState<BrandLearningSummary | null>(
    () => prefetchedBrandLearning?.summary ?? null
  )
  const [brandLearningLoading, setBrandLearningLoading] = useState(false)
  // Preview blobs captured during the pending-business window. Surfaced via
  // pendingBrandLearningPreviews on submit so persistBrandKit can flush them
  // against the real businessId after `patchBrandKit`. Derived from props —
  // the parent's batch hook resolves these before BrandCardStep mounts, so
  // they never change for the lifetime of this component.
  const pendingPreviews = prefetchedBrandLearning?.pendingPreviews ?? []

  const refreshBrandLearningSummary = useCallback(async () => {
    if (!businessId) return
    // Onboarding path is parent-driven — never fetch here, the prefetched
    // summary is the source of truth.
    if (businessId === PENDING_BUSINESS_ID) return
    setBrandLearningLoading(true)
    try {
      const next = await fetchBrandLearning(businessId)
      setBrandLearning(next)
    } catch (err) {
      console.warn('[BrandCardStep] refreshBrandLearningSummary failed:', err)
    } finally {
      setBrandLearningLoading(false)
    }
  }, [businessId])

  // Dedupe the additional-business fetch across StrictMode double-mount.
  // The pending-business branch returns immediately so this is effectively
  // a no-op there.
  const lastFetchKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!businessId || businessId === PENDING_BUSINESS_ID) return
    const key = `${businessId}`
    if (lastFetchKeyRef.current === key) return
    lastFetchKeyRef.current = key
    void refreshBrandLearningSummary()
  }, [refreshBrandLearningSummary, businessId])

  useBrandLearningPusher(() => {
    void refreshBrandLearningSummary()
  }, businessId ?? undefined)

  // Local buffer for chip edits made while `businessId === PENDING_BUSINESS_ID`.
  // The brand-learning endpoints require a real businessId, but the business
  // row is only created on submit (in `persistBrandKit` inside
  // DarkOnboardingFlow). Without this buffer, chip clicks during the pre-
  // submit window either 403'd ("Couldn't save that change") or silently
  // no-op'd. We instead capture them here, render them optimistically as if
  // they were already saved, and emit them via `pendingBrandLearningOverrides`
  // on the submitted `BrandCardData` so the parent can flush them against
  // the real businessId after `createBusinessAction` returns.
  //
  // Keyed by `${source}::${fieldPath}` so a second tap on the same chip
  // replaces the earlier pending value (the last click wins). `value: null`
  // represents a clear; `null` is not a legitimate override value for any
  // chip field, so this dual-purpose encoding is unambiguous.
  const [pendingOverrides, setPendingOverrides] = useState<
    Map<string, PendingBrandLearningOverride>
  >(new Map())

  const isBusinessPending = !businessId || businessId === PENDING_BUSINESS_ID

  const visualSourceFromServer = pickPreferredVisualSource(brandLearning)
  const visualSourceKey: BrandLearningSource = visualSourceFromServer?.source ?? 'instagram'
  const voiceSourceFromServer = pickPreferredVoiceSource(brandLearning)
  const voiceSourceKey: BrandLearningSource = voiceSourceFromServer?.source ?? 'instagram'

  /**
   * Merge buffered overrides into the per-source summary so the chips render
   * with the optimistic values. We construct a synthetic
   * `BrandLearningSourceSummary` rather than mutating the server-fetched
   * object so re-fetches stay deterministic.
   *
   * Pending CLEARs (`value === null`) are applied by stripping the key out
   * of `userOverrides` — same shape the server would return after a DELETE.
   */
  const applyPending = useCallback(
    (
      source: BrandLearningSourceSummary | null,
      sourceKey: BrandLearningSource
    ): BrandLearningSourceSummary | null => {
      if (pendingOverrides.size === 0) return source
      const prefix = `${sourceKey}::`
      const baseOverrides: Record<string, OverrideEntry> = source?.userOverrides
        ? { ...source.userOverrides }
        : {}
      let touched = false
      for (const [key, op] of pendingOverrides) {
        if (!key.startsWith(prefix)) continue
        touched = true
        if (op.value === null) {
          delete baseOverrides[op.fieldPath]
        } else {
          baseOverrides[op.fieldPath] = {
            value: op.value,
            // Placeholder editedAt/editedBy — never displayed; the section
            // only checks the existence of the override + its `value`.
            editedAt: new Date(),
            editedBy: '',
          }
        }
      }
      if (!touched) return source
      if (source) return { ...source, userOverrides: baseOverrides }
      // No server row exists yet for this source — synthesize a minimal one
      // so the section can render the pending edits.
      return {
        source: sourceKey,
        aggregates: null,
        lastRefreshedAt: null,
        refreshHistory: [],
        userOverrides: baseOverrides,
        exemplarMediaUrls: [],
      }
    },
    [pendingOverrides]
  )

  const visualSource = applyPending(visualSourceFromServer, visualSourceKey)
  const voiceSource = applyPending(voiceSourceFromServer, voiceSourceKey)

  const stagePending = useCallback(
    (source: BrandLearningSource, fieldPath: string, value: unknown | null) => {
      setPendingOverrides(prev => {
        const next = new Map(prev)
        next.set(`${source}::${fieldPath}`, { source, fieldPath, value })
        return next
      })
    },
    []
  )

  const handleVisualOverride = useCallback(
    async (fieldPath: string, value: unknown) => {
      if (isBusinessPending) {
        stagePending(visualSourceKey, fieldPath, value)
        return
      }
      try {
        await setBrandLearningOverride(businessId, visualSourceKey, fieldPath, value)
        toast.success('Saved')
        await refreshBrandLearningSummary()
      } catch (err) {
        console.error('[BrandCardStep] setOverride failed:', err)
        toast.error("Couldn't save that change.")
      }
    },
    [businessId, visualSourceKey, refreshBrandLearningSummary, isBusinessPending, stagePending]
  )

  const handleVisualClearOverride = useCallback(
    async (fieldPath: string) => {
      if (isBusinessPending) {
        stagePending(visualSourceKey, fieldPath, null)
        return
      }
      try {
        await clearBrandLearningOverride(businessId, visualSourceKey, fieldPath)
        await refreshBrandLearningSummary()
      } catch (err) {
        console.error('[BrandCardStep] clearOverride failed:', err)
        toast.error("Couldn't reset that field.")
      }
    },
    [businessId, visualSourceKey, refreshBrandLearningSummary, isBusinessPending, stagePending]
  )

  const handleVoiceOverride = useCallback(
    async (fieldPath: string, value: unknown) => {
      if (isBusinessPending) {
        stagePending(voiceSourceKey, fieldPath, value)
        return
      }
      try {
        await setBrandLearningOverride(businessId, voiceSourceKey, fieldPath, value)
        toast.success('Saved')
        await refreshBrandLearningSummary()
      } catch (err) {
        console.error('[BrandCardStep] voice setOverride failed:', err)
        toast.error("Couldn't save that change.")
      }
    },
    [businessId, voiceSourceKey, refreshBrandLearningSummary, isBusinessPending, stagePending]
  )

  const handleVoiceClearOverride = useCallback(
    async (fieldPath: string) => {
      if (isBusinessPending) {
        stagePending(voiceSourceKey, fieldPath, null)
        return
      }
      try {
        await clearBrandLearningOverride(businessId, voiceSourceKey, fieldPath)
        await refreshBrandLearningSummary()
      } catch (err) {
        console.error('[BrandCardStep] voice clearOverride failed:', err)
        toast.error("Couldn't reset that field.")
      }
    },
    [businessId, voiceSourceKey, refreshBrandLearningSummary, isBusinessPending, stagePending]
  )

  const setField = <K extends keyof BrandCardData>(key: K, value: BrandCardData[K]) =>
    setData(prev => ({ ...prev, [key]: value }))

  const toggleTone = (tone: ToneOfVoice) =>
    setData(prev => ({ ...prev, tones: toggleClamped(prev.tones, tone, MAX_TONES) }))

  // Business name is the only realistic blocker (tones default to 'friendly'
  // via ensureDefaults). The min length mirrors the server contract — the
  // createBusiness endpoint 400s on names shorter than MIN_BUSINESS_NAME, so
  // we gate here to catch it before submit rather than after a failed request.
  // When invalid, the name field shows the T3 error border and an inline
  // message sits directly under it.
  const nameInvalid = data.businessName.trim().length < MIN_BUSINESS_NAME
  const canSubmit = !nameInvalid && data.tones.length >= MIN_TONES

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-6">
      <motion.div className="flex flex-col gap-3 px-1" {...fadeInUp(0.5)}>
        <Eyebrow>Confirm your brand</Eyebrow>
        <h2 className="text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[40px]">
          Here's what we found.
        </h2>
        <p className="lede max-w-[520px] !text-[15px] !leading-[1.55]">
          Tap any field to edit. We'll keep learning as you create.
        </p>
        {scanError && (
          <div className="alert mt-1" data-tone="warning" role="status">
            {scanErrorCopy(scanSource)}
          </div>
        )}
      </motion.div>

      {/* No fade on the card itself — opacity would cascade to the shared
          layoutId children (logo, name, swatches, active tone chips), making
          them invisible during their flight from the scanning screen. The
          card chrome appears instantly; the user's eye is on the flying
          elements, not the border. */}
      <div className="card flex flex-col gap-5 p-6 sm:p-8">
        <header className="flex items-start gap-4">
          <LogoSlot
            logoUrl={data.logoUrl}
            businessName={data.businessName}
            logoBackdrop={data.logoBackdrop}
            shareLayoutId={!morphSettled}
          />
          <div className="flex flex-1 flex-col gap-1.5">
            {/* Name + its error are grouped so the column's gap-1.5 stays
                constant; the error animates its own height (incl. pt-1) in/out,
                so toggling validity doesn't snap the description below it. */}
            <div>
              <EditableValue
                value={data.businessName}
                onChange={v => setField('businessName', v)}
                placeholder="Business name"
                label="Business name"
                singleLine
                invalid={nameInvalid}
                // The value itself carries the shared layoutId so it can fly in
                // from ScanningStep, but a layoutId cannot ride a textarea's
                // text node. So for the ~800ms the morph is in flight, a
                // motion.span stands in on top of the field. Same typography
                // context, same origin, so the handover is metrically silent.
                overlay={
                  !morphSettled && data.businessName ? (
                    <motion.span layoutId={SHARED_LAYOUT_NAME} transition={TRANSITION_NAME}>
                      {data.businessName}
                    </motion.span>
                  ) : undefined
                }
                className="text-[24px] leading-[1.1] font-semibold tracking-[-0.02em] text-paper"
              />
              <AnimatePresence initial={false}>
                {nameInvalid && (
                  <motion.p
                    key="name-error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                    className="overflow-hidden pt-1 text-[12px] text-[color:var(--alert-error-text)]"
                    role="status"
                    data-testid="onboarding-confirm-block-reason"
                  >
                    Business name must be at least {MIN_BUSINESS_NAME} characters
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
            <EditableValue
              value={data.description}
              onChange={v => setField('description', v)}
              placeholder="One-line description of what you do"
              label="Business description"
              maxLines={6}
              className="text-[14px] leading-[1.5] text-paper-dim"
            />
          </div>
        </header>

        <BrandVisualSection
          summary={visualSource}
          isLoading={brandLearningLoading}
          onOverride={handleVisualOverride}
          onClearOverride={handleVisualClearOverride}
          variant="card"
        />

        {data.colors && <ColorsRow colors={data.colors} shareLayoutId={!morphSettled} />}

        <Row label="Industry" labelId={INDUSTRY_LABEL_ID}>
          <EditableValue
            value={data.industry}
            onChange={v => setField('industry', v)}
            placeholder="e.g. Bakery, SaaS, Photography"
            labelledBy={INDUSTRY_LABEL_ID}
            singleLine
            className="text-[14px] text-paper"
          />
        </Row>

        <Row label="Audience" labelId={AUDIENCE_LABEL_ID}>
          <EditableValue
            value={data.targetAudience}
            onChange={v => setField('targetAudience', v)}
            placeholder="Who do you serve?"
            labelledBy={AUDIENCE_LABEL_ID}
            maxLines={5}
            className="text-[14px] text-paper"
          />
        </Row>

        <Row label="Tone">
          <div className="flex flex-wrap gap-2">
            {TONE_OF_VOICE_OPTIONS.map(opt => {
              const isActive = data.tones.includes(opt.id)
              return (
                <Chip
                  key={opt.id}
                  active={isActive}
                  onClick={() => toggleTone(opt.id)}
                  label={opt.label}
                  // Active chips share a layoutId with the scanning "Voice"
                  // row so they fly into place — but only until the morph
                  // settles. After that we drop the layoutId so chips don't
                  // orphan into the next phase. Inactive chips never share.
                  layoutId={isActive && !morphSettled ? SHARED_LAYOUT_TONE(opt.id) : undefined}
                />
              )
            })}
          </div>
          <Hint text={`Pick up to ${MAX_TONES}. We picked ${data.tones.length} for you.`} />
        </Row>

        <BrandVoiceSection
          summary={voiceSource}
          isLoading={brandLearningLoading}
          onOverride={handleVoiceOverride}
          onClearOverride={handleVoiceClearOverride}
          variant="card"
        />
      </div>

      <MobileStickyFooter>
        {/* motion.div needs `w-full` so the cta-sized Button (w-full
            inside) renders at the right width — items-center on the
            outer footer would otherwise collapse this to auto. */}
        <motion.div className="flex w-full flex-col items-center gap-3" {...fadeInUp(0.85)}>
          <Button
            variant="t3-primary"
            size="t3-lg"
            disabled={!canSubmit || isSubmitting}
            aria-busy={isSubmitting}
            onClick={() => {
              // Build the submission payload — only attach optional fields when
              // there's actually something to attach, so the diff stays clean.
              const pendingOverridesList = Array.from(pendingOverrides.values())
              let submission: BrandCardData = data
              if (pendingOverridesList.length > 0) {
                submission = { ...submission, pendingBrandLearningOverrides: pendingOverridesList }
              }
              if (pendingPreviews.length > 0) {
                submission = { ...submission, pendingBrandLearningPreviews: pendingPreviews }
              }
              onSubmit(submission)
            }}
            className="!max-w-[420px]"
            data-testid="onboarding-confirm-submit"
          >
            {isSubmitting
              ? 'Saving…'
              : nextIsChat
                ? "Looks right, let's talk about your brand →"
                : 'Looks good, let me in →'}
          </Button>
          <p className="text-center text-[12px] text-paper-faint">
            {nextIsChat
              ? 'Next, a quick chat so your posts sound like you. You can edit your brand kit anytime.'
              : "Don't worry about getting it perfect. You can edit your brand kit anytime."}
          </p>
        </motion.div>
      </MobileStickyFooter>
    </div>
  )
}

const LogoSlot: React.FC<{
  logoUrl?: string | null
  businessName: string
  /** Backdrop for the logo chip. 'dark' (opaque/colorful logos) sits on the
   *  on-brand dark T3 chip; 'light'/undefined keeps a light plate so dark
   *  transparent wordmarks stay legible. */
  logoBackdrop?: LogoBackdrop
  /** True only while the ScanningStep→BrandCardStep morph is in flight. After
   *  the morph settles, the layoutId is dropped so this logo can unmount
   *  cleanly when BrandCardStep leaves (see `morphSettled` in the parent). */
  shareLayoutId: boolean
}> = ({ logoUrl, businessName, logoBackdrop, shareLayoutId }) => {
  const [imgBroken, setImgBroken] = useState(false)
  const initial = businessName.trim().charAt(0).toUpperCase() || '·'

  if (logoUrl && !imgBroken) {
    return (
      <div
        className={cn(
          'flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-line-bright p-1.5',
          logoBackdrop === 'dark' ? 'bg-bg-3' : 'bg-white'
        )}
      >
        <motion.img
          layoutId={shareLayoutId ? SHARED_LAYOUT_LOGO : undefined}
          src={logoUrl}
          alt="Brand logo"
          className="h-full w-full rounded-[6px] object-contain"
          transition={TRANSITION_LOGO}
          onError={() => setImgBroken(true)}
        />
      </div>
    )
  }
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[10px] border border-line-bright bg-bg-3 text-[20px] font-semibold text-paper">
      {initial}
    </div>
  )
}

const Row: React.FC<{ label: string; labelId?: string; children: React.ReactNode }> = ({
  label,
  labelId,
  children,
}) => (
  <div className="editable-row">
    <div className="row-label" id={labelId}>
      {label}
    </div>
    <div className="row-value">{children}</div>
  </div>
)

const Hint: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-[11px] text-paper-faint">{text}</p>
)

const Chip: React.FC<{
  active: boolean
  onClick: () => void
  label: string
  layoutId?: string
}> = ({ active, onClick, label, layoutId }) => {
  if (layoutId) {
    return (
      <motion.button
        layoutId={layoutId}
        type="button"
        onClick={onClick}
        className="chip"
        data-active={active}
        aria-pressed={active}
        transition={TRANSITION_TONE}
      >
        {label}
      </motion.button>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="chip"
      data-active={active}
      aria-pressed={active}
    >
      {label}
    </button>
  )
}

const ColorsRow: React.FC<{
  colors: NonNullable<BrandCardData['colors']>
  /** True only while the ScanningStep→BrandCardStep morph is in flight.
   *  See `morphSettled` in BrandCardStep for the rationale. */
  shareLayoutId: boolean
}> = ({ colors, shareLayoutId }) => {
  return (
    <Row label="Brand colors">
      <div className="flex items-center gap-3">
        {(['primary', 'secondary', 'accent'] as const).map(key => (
          <div key={key} className="flex items-center gap-2">
            <motion.span
              layoutId={shareLayoutId ? SHARED_LAYOUT_COLOR(key) : undefined}
              className="inline-block h-7 w-7 rounded-[8px] border border-line-bright"
              style={{ background: colors[key] }}
              aria-label={`${key} ${colors[key]}`}
              transition={TRANSITION_COLOR(key)}
            />
            <motion.span
              className="mono text-[10px] tracking-[0.04em] text-paper-faint uppercase"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              {colors[key].replace(/^#/, '')}
            </motion.span>
          </div>
        ))}
      </div>
    </Row>
  )
}

function scanErrorCopy(source: BrandCardStepProps['scanSource']): string {
  if (source === 'social') {
    return "We couldn't pull data from that handle. Instagram only exposes Business and Creator accounts to us, so fill in the basics and we'll learn the rest as you create."
  }
  if (source === 'website') {
    return "We couldn't fully read that site. Some sites block scanners, so fill in what you know and you can edit later."
  }
  return "Fill in what you know about your business, and we'll keep learning as you create."
}

function toggleClamped<T>(arr: T[], value: T, max: number): T[] {
  if (arr.includes(value)) return arr.filter(v => v !== value)
  if (arr.length >= max) return [...arr.slice(1), value]
  return [...arr, value]
}

function ensureDefaults(initial: BrandCardData): BrandCardData {
  const tones = initial.tones.length > 0 ? initial.tones : (['friendly'] as ToneOfVoice[])
  const goals = initial.goals.length > 0 ? initial.goals : (['grow_audience'] as GeneralGoal[])
  return { ...initial, tones, goals }
}
